import { useEffect } from 'react';
import { useApp } from '@/lib/core/providers/appProvider';
import { useClientTranslation } from '@/lib/core/i18n';
import { ModuleProperties } from '@/lib/core/types';
import { cn, ImagesToImageMap, LinksMap } from '@/lib/core/utils';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import { useActiveRideStore } from '@/lib/core/stores/useActiveRideStore';
import { HomePageHeroBannerElementModel } from '@/services/platform/api/model';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import ModuleWrapper from '@/components/shared/module/wrapper';
import { BackgroundVideo } from '@/components/shared/video/background';
import { RideProgressBanner } from './rideProgressBanner';
import { isRideInProgress } from '@/services/platform/data/booking/rides/rideStatus';

export const HomePageHeroBanner = (props: ModuleProperties) => {
  const content = props.content as HomePageHeroBannerElementModel | undefined;
  const properties = content?.properties;
  const { user, membership } = useApp();
  const t = useClientTranslation();
  const setMainClassName = useUIStore(state => state.setMainClassName);
  const activeRide = useActiveRideStore(state => state.ride);
  const showRideBanner = !!activeRide?.driver && isRideInProgress(activeRide.status);
  const hasActiveMembership = user?.hasActiveMembership;
  const hasCard = hasActiveMembership && (membership?.hasCard ?? membership?.cardId != null);

  useEffect(() => {
    if (!hasActiveMembership) {
      setMainClassName("bg-[url('/images/jncquoi_bg_nonmember.png')] bg-size-[100%] bg-top bg-no-repeat");
    }
    return () => setMainClassName('');
  }, [hasActiveMembership, setMainClassName]);

  const firstName = hasCard ? (user?.name?.split(' ')[0] ?? null) : null;
  const heroTitle = properties?.title ?? t('App.Home.HeroDefaultTitle');
  const bgImage = ImagesToImageMap(properties?.backgroundImage);
  const isVideo = bgImage?.type === 'umbracoMediaVideo';
  const buttons = LinksMap(properties?.buttons ?? []);
  const solidButtons = buttons.slice(0, 2);
  const outlineButtons = buttons.slice(2);

  return (
    <ModuleWrapper
      component={props}
      isFullWidth={true}
      style={
        hasActiveMembership
          ? bgImage?.src && !isVideo
            ? {
                backgroundImage: `url(${bgImage.src})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : { background: 'linear-gradient(to bottom, #2a1a0e, #1a1a1a)' }
          : undefined
      }
      classNames={{
        base: cn(
          'relative overflow-hidden',
          hasActiveMembership ? 'min-h-[80svh] justify-end' : 'justify-center'
        ),
        wrapper: cn(
          'pb-10 lg:pb-10',
          hasActiveMembership ? 'pt-28 lg:pt-28' : 'pt-[calc(max(1rem,env(safe-area-inset-top))+60px)]'
        ),
      }}
      beforeChildren={hasActiveMembership && isVideo ? <BackgroundVideo src={bgImage?.src} /> : undefined}
    >
      <div className={cn('col-span-2 lg:col-span-12', !hasActiveMembership && 'pt-15')}>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={
            hasActiveMembership
              ? { background: 'linear-gradient(to bottom, rgba(0,0,0,0.07) 34%, rgba(0,0,0,0.7) 75%)' }
              : undefined
          }
        />

        <div className="relative z-10 flex w-full flex-col gap-8">
          {showRideBanner ? (
            <RideProgressBanner firstName={firstName} />
          ) : (
            <div className="flex flex-col items-center leading-none">
              {firstName && (
                <span
                  className="font-didot text-[36px] text-white italic"
                  style={{ letterSpacing: '-1.44px', lineHeight: '34px' }}
                >
                  {firstName}
                </span>
              )}
              <span className="font-pp-neue-montreal xxs:text-4xl text-center text-3xl font-semibold tracking-[0.5px] text-white uppercase">
                {heroTitle}
              </span>
            </div>
          )}

          {buttons.length > 0 && (
            <div className="flex w-full flex-col gap-2 px-4">
              {solidButtons.length > 0 && (
                <div className="flex gap-2">
                  {solidButtons.map(btn => (
                    <Button
                      key={`${btn.href}-${btn.label}-${btn.route?.path}`}
                      asChild
                      variant="primary-solid"
                      palette="white"
                      className="flex-1 shrink normal-case"
                    >
                      <Link to={btn.href}>{btn.label}</Link>
                    </Button>
                  ))}
                </div>
              )}
              {outlineButtons.map(btn => (
                <Button
                  key={`${btn.href}-${btn.label}`}
                  asChild
                  variant="secondary-outline"
                  palette="white"
                  className="w-full normal-case"
                >
                  <Link to={btn.href}>{btn.label}</Link>
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    </ModuleWrapper>
  );
};
